import React, { useState } from 'react';

const DeliveryDetailsModal = ({ delivery, onClose, onStatusUpdate, getRiderName }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingData, setAccountingData] = useState(null);
  const [isAccountingProcessing, setIsAccountingProcessing] = useState(false);

  const handleConfirmHandover = async (deliveryId) => {
    setIsUpdating(true);
    try {
      // Call the Cloud Function to confirm handover
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../lib/firebase');
      
      const confirmHandover = httpsCallable(functions, 'confirmRemittanceHandover');
      const result = await confirmHandover({ deliveryId });
      
      console.log('Handover confirmation result:', result.data);
      
      // Check if accounting review is required
      if (result.data.requiresAccountingReview && result.data.orderData) {
        // Add slight delay before showing modal
        setTimeout(() => {
          setAccountingData(result.data);
          setShowAccountingModal(true);
        }, 500);
        // Don't close the main modal yet - wait for accounting confirmation
      } else {
        // Close modal and refresh
        onClose();
      }
    } catch (error) {
      console.error('Error confirming handover:', error);
      alert('Error confirming handover: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAccountingConfirmation = async () => {
    if (!accountingData) return;
    
    setIsAccountingProcessing(true);
    try {
      // Call the Cloud Function to complete the delivery without accounting posting
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../lib/firebase');
      
      const completeDelivery = httpsCallable(functions, 'completeDeliveryAfterHandover');
      
      // Call the function to complete the delivery
      const result = await completeDelivery({
        deliveryId: accountingData.deliveryId,
        note: 'Payment completed via remittance handover confirmation'
      });
      
      console.log('Delivery completion result:', result.data);
      
      // Close both modals and refresh
      setShowAccountingModal(false);
      setAccountingData(null);
      onClose();
      
      // Show success message
      alert('Remittance handover confirmed successfully! Delivery marked as completed. Accounting will be handled via daily summary workflow.');
    } catch (error) {
      console.error('Error completing delivery:', error);
      alert('Error completing delivery: ' + error.message);
    } finally {
      setIsAccountingProcessing(false);
    }
  };

  const handleAccountingCancel = async () => {
    if (!accountingData) return;
    
    try {
      // Call the Cloud Function to revert the handover confirmation
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../lib/firebase');
      
      const revertHandover = httpsCallable(functions, 'revertHandoverConfirmation');
      
      // Call the function to revert the status
      const result = await revertHandover({ deliveryId: accountingData.deliveryId });
      
      console.log('Handover reversion result:', result.data);
      
      // Close the accounting modal
      setShowAccountingModal(false);
      setAccountingData(null);
      
      // Show message to user
      alert('Handover confirmation has been cancelled. The delivery is back to remittance_pending status.');
      
      // Close the main modal as well
      onClose();
    } catch (error) {
      console.error('Error cancelling accounting:', error);
      alert('Error cancelling accounting: ' + error.message);
    }
  };

  const handlePrepaidReview = async (action) => {
    if (!delivery) return;
    
    setIsUpdating(true);
    try {
      // Call the Cloud Function to review prepaid delivery
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../lib/firebase');
      
      const reviewPrepaidDelivery = httpsCallable(functions, 'reviewPrepaidDelivery');
      
      // Get notes from user if rejecting
      let notes = '';
      if (action === 'reject') {
        notes = prompt('Please provide a reason for rejection:') || 'No reason provided';
        if (notes === null) return; // User cancelled
      }
      
      const result = await reviewPrepaidDelivery({
        deliveryId: delivery.id,
        action: action,
        notes: notes
      });
      
      console.log('Prepaid delivery review result:', result.data);
      
      // Show success message
      if (action === 'approve') {
        alert('Delivery approved successfully! Rider wallet credited and posted to accounting.');
      } else {
        alert('Delivery rejected. Status reverted to out_for_delivery.');
      }
      
      // Close modal - dashboard will refresh automatically through Firestore listener
      onClose();
    } catch (error) {
      console.error('Error reviewing prepaid delivery:', error);
      alert('Error reviewing delivery: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return timestamp.toDate ? timestamp.toDate().toLocaleString() : new Date(timestamp).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'assigned': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-purple-100 text-purple-800',
      'for_review': 'bg-orange-100 text-orange-800',
      'remittance_pending': 'bg-orange-100 text-orange-800',
      'handover_confirmed': 'bg-indigo-100 text-indigo-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Delivery Details</h2>
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Basic Information */}
          <div className="space-y-6">
            {/* Order Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Order ID:</span>
                  <span className="text-sm text-gray-900">{delivery.orderId || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Customer:</span>
                  <span className="text-sm text-gray-900">{delivery.customerName || delivery.buyerName || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Email:</span>
                  <span className="text-sm text-gray-900">{delivery.customerEmail || delivery.buyerName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Phone:</span>
                  <span className="text-sm text-gray-900">{delivery.customerPhone || delivery.phone || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Address:</span>
                  <span className="text-sm text-gray-900">{delivery.address || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Financial Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-blue-700">COD Amount:</span>
                  <span className="text-sm font-semibold text-blue-900">₱{Number(delivery.codAmount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-blue-700">Total Remitted:</span>
                  <span className="text-sm font-semibold text-blue-900">
                    ₱{delivery.remittances?.reduce((sum, r) => sum + Number(r.amount || 0), 0).toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-blue-700">Remittance Count:</span>
                  <span className="text-sm font-semibold text-blue-900">{delivery.remittances?.length || 0}</span>
                </div>
                {delivery.overweightFee && delivery.overweightFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-blue-700">Overweight Fee:</span>
                    <span className="text-sm font-semibold text-blue-900">₱{Number(delivery.overweightFee).toFixed(2)}</span>
                  </div>
                )}
                
                {/* Prepaid Delivery Payment Details */}
                {delivery.status === 'for_review' && delivery.riderPaymentAmount && (
                  <>
                    <div className="border-t border-blue-200 pt-2 mt-2">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">Rider Payment Details</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-blue-700">Base Payment:</span>
                          <span className="text-sm font-semibold text-blue-900">₱{Number(delivery.riderPaymentBase || 45).toFixed(2)}</span>
                        </div>
                        {delivery.riderPaymentBonus && delivery.riderPaymentBonus > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-blue-700">Weight Bonus:</span>
                            <span className="text-sm font-semibold text-blue-900">₱{Number(delivery.riderPaymentBonus).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-blue-700">Total Rider Payment:</span>
                          <span className="text-sm font-semibold text-blue-900 text-lg">₱{Number(delivery.riderPaymentAmount).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Rider Information */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Rider Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-green-700">Rider Name:</span>
                  <span className="text-sm text-green-900">{getRiderName ? getRiderName(delivery.riderId) : (delivery.riderName || 'Unassigned')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-green-700">Assigned Date:</span>
                  <span className="text-sm text-green-900">{formatDate(delivery.assignedAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-green-700">COD Collected:</span>
                  <span className="text-sm text-green-900">{formatDate(delivery.codCollectedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Status and Timeline */}
          <div className="space-y-6">
            {/* Current Status */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Status</h3>
              <div className="mb-4">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(delivery.status)}`}>
                  {delivery.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                </span>
              </div>
              
              {/* Remittance Handover Confirmation */}
              {delivery.status === 'remittance_pending' && delivery.codAmount && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800 mb-3">
                      <strong>COD Amount:</strong> ₱{Number(delivery.codAmount).toFixed(2)}
                    </p>
                    <p className="text-xs text-blue-700">
                      This button confirms that the rider has handed over the COD payment to store staff.
                    </p>
                  </div>
                  <button
                    onClick={() => handleConfirmHandover(delivery.id)}
                    disabled={isUpdating}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {isUpdating ? 'Confirming...' : 'Confirm Remittance Handover'}
                  </button>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Delivery Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created</p>
                    <p className="text-xs text-gray-500">{formatDate(delivery.createdAt)}</p>
                  </div>
                </div>
                
                {delivery.assignedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Assigned to Rider</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.assignedAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.codCollectedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">COD Collected</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.codCollectedAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.lastRemittanceAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Last Remittance</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.lastRemittanceAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.handoverConfirmedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Remittance Handover Confirmed</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.handoverConfirmedAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.completedAt && delivery.status === 'for_review' && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Delivery Completed (Pending Review)</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.completedAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.approvedAt && delivery.status === 'completed' && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Delivery Approved & Wallet Credited</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.approvedAt)}</p>
                    </div>
                  </div>
                )}

                {delivery.updatedAt && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-gray-600 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Last Updated</p>
                      <p className="text-xs text-gray-500">{formatDate(delivery.updatedAt)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Remittance History */}
            {delivery.remittances && delivery.remittances.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Remittance History</h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {delivery.remittances.map((remittance, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded text-xs">
                      <div>
                        <span className="font-medium">#{index + 1}</span>
                        <span className="text-gray-500 ml-2">
                          {formatDate(remittance.timestamp)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">₱{Number(remittance.amount).toFixed(2)}</span>
                        <span className="text-gray-500 ml-2">
                          by {remittance.cashierId || 'Rider'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          {/* Prepaid Delivery Review Buttons - Only show for 'for_review' status */}
          {delivery.status === 'for_review' && (
            <div className="flex gap-2 mr-auto">
              <button
                onClick={() => handlePrepaidReview('reject')}
                disabled={isUpdating}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Reject Delivery
              </button>
              <button
                onClick={() => handlePrepaidReview('approve')}
                disabled={isUpdating}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                Approve & Complete
              </button>
            </div>
          )}
          
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Accounting Review Modal */}
      {showAccountingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1201]">
          <div className="bg-white rounded-lg p-4 w-full max-w-sm mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Accounting Review</h2>
              <button
                onClick={handleAccountingCancel}
                disabled={isAccountingProcessing}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Order Details</h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="font-medium text-blue-700">Order ID:</span>
                    <span className="text-blue-900">{accountingData?.orderData?.orderId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-blue-700">Customer:</span>
                    <span className="text-blue-900">{accountingData?.orderData?.buyerName || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-blue-700">Phone:</span>
                    <span className="text-blue-900">{accountingData?.orderData?.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-green-900 mb-2">Payment Details</h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="font-medium text-green-700">COD Amount:</span>
                    <span className="font-semibold text-green-900">₱{Number(accountingData?.orderData?.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-green-700">Delivery Fee:</span>
                    <span className="font-semibold text-green-900">₱{Number(accountingData?.orderData?.deliveryFee || 0).toFixed(2)}</span>
                  </div>
                  {accountingData?.orderData?.overweightFee && accountingData.orderData.overweightFee > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium text-green-700">Overweight Fee:</span>
                      <span className="font-semibold text-green-900">₱{Number(accountingData.orderData.overweightFee).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-medium text-green-700">Total Amount:</span>
                    <span className="font-semibold text-green-900">₱{Number(accountingData?.orderData?.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-purple-900 mb-2">Delivery Info</h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="font-medium text-purple-700">Delivery ID:</span>
                    <span className="text-purple-900">{accountingData?.deliveryId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-purple-700">Store Payment ID:</span>
                    <span className="text-purple-900">{accountingData?.storePaymentId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-purple-700">Status:</span>
                    <span className="text-purple-900">Handover Confirmed</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Delivery Completion</h3>
                <div className="space-y-1 text-xs">
                  <p className="text-blue-800">
                    This will complete the delivery and mark it as finished.
                  </p>
                  <p className="text-blue-700 mt-1">
                    <strong>Note:</strong> Accounting entries are now handled via the daily summary workflow, not individual deliveries.
                  </p>
                </div>
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={handleAccountingCancel}
                  disabled={isAccountingProcessing}
                  className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAccountingConfirmation}
                  disabled={isAccountingProcessing}
                  className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 text-sm"
                >
                  {isAccountingProcessing ? 'Processing...' : 'Complete Delivery'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDetailsModal;
